import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Panel } from "@/components/kit";
import { Markdown } from "@/components/Markdown";
import { money, num, LEAD_COLORS, healthColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Flame, Building2, MousePointerClick, LogOut, Loader2, BookOpen, BarChart2,
  Sliders, Plus, Trash2, Sparkles, LayoutDashboard, Mail, UserPlus, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "businesses", label: "Businesses", icon: Building2 },
  { id: "knowledge", label: "Knowledge Base", icon: BookOpen },
  { id: "benchmarks", label: "Benchmarks", icon: BarChart2 },
  { id: "config", label: "Reco Engine", icon: Sliders },
  { id: "admins", label: "Admins", icon: ShieldCheck },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background mm-grid-bg">
      <header className="sticky top-0 z-30 flex items-center justify-between px-5 sm:px-8 h-16 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <Logo size="sm" />
          <span className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-rose-400 border border-rose-500/30 bg-rose-500/10 rounded-full px-2.5 py-1">Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-xs text-slate-500 font-mono">{user?.email}</span>
          <Button variant="ghost" onClick={doLogout} data-testid="admin-logout" className="text-slate-400 hover:text-rose-300"><LogOut className="h-4 w-4 mr-2" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex gap-1.5 overflow-x-auto mb-8 mm-scroll">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm whitespace-nowrap border transition-colors ${tab === t.id ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "businesses" && <Businesses />}
        {tab === "knowledge" && <Knowledge />}
        {tab === "benchmarks" && <Benchmarks />}
        {tab === "config" && <RecoConfig />}
        {tab === "admins" && <Admins />}
      </div>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Center />;
  const cards = [
    { label: "Emails Captured", value: d.emails_captured ?? d.users, icon: Mail, color: "text-emerald-400" },
    { label: "Landing Leads", value: d.landing_leads, icon: UserPlus, color: "text-amber-400" },
    { label: "Active (7d)", value: d.active, icon: Users, color: "text-sky-400" },
    { label: "New (7d)", value: d.new, icon: Users, color: "text-violet-400" },
    { label: "Businesses", value: d.businesses, icon: Building2, color: "text-violet-400" },
    { label: "Qualified Leads", value: d.qualified_leads, icon: Flame, color: "text-emerald-400" },
    { label: "HOT Leads", value: d.hot_leads, icon: Flame, color: "text-rose-400" },
    { label: "Student Magnet Clicks", value: d.student_magnet_clicks, icon: MousePointerClick, color: "text-emerald-400" },
    { label: "Member Magnet Clicks", value: d.member_magnet_clicks, icon: MousePointerClick, color: "text-sky-400" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mm-fade-in">
      {cards.map((c) => (
        <Panel key={c.label} className="!p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-mono">{c.label}</span>
            <c.icon className={`h-4 w-4 ${c.color}`} />
          </div>
          <div className="text-3xl font-bold font-display" data-testid={`admin-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{num(c.value)}</div>
        </Panel>
      ))}
    </div>
  );
}

function Businesses() {
  const [rows, setRows] = useState(null);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/admin/businesses").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const viewIntel = async (bid, name) => {
    setOpen(true); setIntel({ name, brief: "" }); setIntelLoading(true);
    try { const { data } = await api.get(`/admin/business/${bid}/sales-intel`); setIntel({ name, ...data }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setIntelLoading(false); }
  };

  if (rows === null) return <Center />;
  if (!rows.length) return <Panel><p className="text-sm text-slate-400 text-center py-8">No owner accounts yet. New signups will appear here.</p></Panel>;

  return (
    <Panel className="!p-0 overflow-hidden mm-fade-in">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 font-mono border-b border-slate-800">
              {["School", "Location", "Type", "Members", "Revenue", "Ad Spend", "Health", "Lead", ""].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid="admin-business-row">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-100">{r.business_name || r.name || "—"}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{r.location || "—"}</td>
                <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{r.type || "—"}</td>
                <td className="px-4 py-3 text-slate-300">{num(r.members)}</td>
                <td className="px-4 py-3 text-slate-300">{money(r.revenue)}</td>
                <td className="px-4 py-3 text-slate-300">{money(r.ad_spend)}</td>
                <td className={`px-4 py-3 font-mono ${healthColor(r.health_score)}`}>{r.health_score ?? "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${LEAD_COLORS[r.lead_score] || LEAD_COLORS.LOW}`}>{r.lead_score}</span></td>
                <td className="px-4 py-3">
                  <Button size="sm" data-testid="sales-intel-btn" onClick={() => viewIntel(r.business_id, r.business_name || r.name)} className="bg-slate-800 hover:bg-slate-700 text-emerald-300">
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Intel
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto mm-scroll">
          <DialogHeader><DialogTitle className="font-display">Sales Intelligence — {intel?.name}</DialogTitle>
            <DialogDescription className="sr-only">AI-generated sales intelligence brief for this prospect.</DialogDescription>
          </DialogHeader>
          {intel?.lead_score && <div className="mb-2"><span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${LEAD_COLORS[intel.lead_score]}`}>{intel.lead_score}</span></div>}
          {intelLoading ? <div className="flex items-center gap-2 text-slate-400 py-6"><Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> Generating brief…</div>
            : <Markdown className="text-sm text-slate-200" data-testid="sales-intel-content">{intel?.brief}</Markdown>}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function Knowledge() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", content: "", category: "strategy" });
  const load = () => api.get("/admin/knowledge").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content required.");
    try { await api.post("/admin/knowledge", form); setForm({ title: "", content: "", category: "strategy" }); load(); toast.success("Added to knowledge base."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/knowledge/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6 mm-fade-in">
      <Panel>
        <div className="text-sm font-medium text-slate-300 mb-4">Add proprietary strategy / script / offer</div>
        <div className="space-y-3">
          <Input data-testid="kb-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. 5-min speed-to-lead script)" className="bg-slate-950 border-slate-700 h-10" />
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="bg-slate-950 border-slate-700 h-10" />
          <Textarea data-testid="kb-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="The strategy, script or sequence the AI should prioritize…" className="bg-slate-950 border-slate-700 min-h-32" />
          <Button data-testid="kb-add-btn" onClick={add} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-2" /> Add entry</Button>
        </div>
      </Panel>
      <Panel>
        <div className="text-sm font-medium text-slate-300 mb-4">Knowledge base ({items.length})</div>
        {items.length === 0 ? <p className="text-sm text-slate-500">Empty. Add your first entry — the coach prioritizes these.</p> : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto mm-scroll">
            {items.map((it) => (
              <div key={it.id} className="bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3" data-testid="kb-item">
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium text-slate-100">{it.title}</div><div className="text-xs text-slate-500 mb-1">{it.category}</div></div>
                  <button onClick={() => del(it.id)} className="text-slate-500 hover:text-rose-400 p-1 shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
                <p className="text-sm text-slate-400 line-clamp-3">{it.content}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Benchmarks() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ metric: "", segment: "", low: "", high: "" });
  const load = () => api.get("/admin/benchmarks").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.metric.trim() || !form.segment.trim()) return toast.error("Metric and segment required.");
    try { await api.post("/admin/benchmarks", { ...form, low: Number(form.low), high: Number(form.high) }); setForm({ metric: "", segment: "", low: "", high: "" }); load(); toast.success("Benchmark saved."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/benchmarks/${id}`); load(); };
  return (
    <div className="mm-fade-in">
      <Panel className="mb-6">
        <div className="text-sm font-medium text-slate-300 mb-4">Add benchmark (segmentable by type / program / source)</div>
        <div className="grid sm:grid-cols-5 gap-3">
          <Input data-testid="bm-metric" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} placeholder="Metric (CPL)" className="bg-slate-950 border-slate-700 h-10" />
          <Input value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} placeholder="Segment (BJJ / Facebook)" className="bg-slate-950 border-slate-700 h-10" />
          <Input type="number" value={form.low} onChange={(e) => setForm({ ...form, low: e.target.value })} placeholder="Low" className="bg-slate-950 border-slate-700 h-10" />
          <Input type="number" value={form.high} onChange={(e) => setForm({ ...form, high: e.target.value })} placeholder="High" className="bg-slate-950 border-slate-700 h-10" />
          <Button data-testid="bm-add-btn" onClick={add} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-2" /> Add</Button>
        </div>
      </Panel>
      <Panel>
        {items.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">No benchmarks yet.</p> : (
          <div className="space-y-2">
            {items.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3" data-testid="bm-item">
                <div className="flex items-center gap-4"><span className="font-medium text-slate-100">{b.metric}</span><span className="text-xs text-slate-500 font-mono">{b.segment}</span></div>
                <div className="flex items-center gap-3"><span className="font-mono text-emerald-400">{b.low} – {b.high}</span>
                  <button onClick={() => del(b.id)} className="text-slate-500 hover:text-rose-400 p-1"><Trash2 className="h-4 w-4" /></button></div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function RecoConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/reco-config").then((r) => setCfg(r.data)).catch(() => setCfg(false)); }, []);
  if (cfg === null) return <Center />;
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/reco-config", cfg); toast.success("Recommendation engine updated for all businesses."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const nums = [
    ["min_messages_before_reco", "Min AI replies before a CTA can show"],
    ["max_recos_per_conversation", "Max CTAs per conversation"],
    ["max_recos_per_week", "Max CTAs per week"],
  ];
  const scores = ["LOW", "MEDIUM", "HIGH", "HOT"];
  return (
    <Panel className="max-w-2xl mm-fade-in">
      <div className="text-sm font-medium text-slate-300 mb-1">Recommendation Engine (frequency caps & triggers)</div>
      <p className="text-xs text-slate-500 mb-5">Tune aggressiveness without redeploying. Applies to all businesses.</p>
      <div className="flex items-center justify-between mb-5">
        <Label>Engine enabled</Label>
        <button data-testid="reco-enabled-toggle" onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
          className={`h-6 w-11 rounded-full transition-colors relative ${cfg.enabled ? "bg-emerald-500" : "bg-slate-700"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${cfg.enabled ? "left-5.5" : "left-0.5"}`} style={{ left: cfg.enabled ? "1.375rem" : "0.125rem" }} />
        </button>
      </div>
      {nums.map(([k, label]) => (
        <div key={k} className="flex items-center justify-between gap-4 mb-4">
          <Label className="text-sm text-slate-300">{label}</Label>
          <Input data-testid={`reco-${k}`} type="number" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: Number(e.target.value) })} className="bg-slate-950 border-slate-700 h-10 w-24" />
        </div>
      ))}
      {[["min_lead_score", "Min lead score before showing the offer"]].map(([k, label]) => (
        <div key={k} className="flex items-center justify-between gap-4 mb-4">
          <Label className="text-sm text-slate-300">{label}</Label>
          <div className="flex gap-1">
            {scores.map((s) => (
              <button key={s} data-testid={`reco-score-${s}`} data-selected={cfg[k] === s} onClick={() => setCfg({ ...cfg, [k]: s })}
                className={`text-xs font-mono px-2.5 py-1 rounded-lg border ${cfg[k] === s ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-slate-800 text-slate-500"}`}>{s}</button>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-500 mb-4">Offer shown is auto-selected by business type: martial arts → Student Magnet+, gyms → Member Magnet+.</p>
      <Button data-testid="reco-save-btn" onClick={save} disabled={saving} className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save configuration"}
      </Button>
    </Panel>
  );
}

function Admins() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/admins").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.email.trim() || form.password.length < 8) return toast.error("Email and a password of at least 8 characters are required.");
    setSaving(true);
    try { await api.post("/admin/admins", form); setForm({ email: "", name: "", password: "" }); load(); toast.success("Admin added."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id, email) => {
    if (!window.confirm(`Remove admin ${email}? They'll lose access immediately.`)) return;
    try { await api.delete(`/admin/admins/${id}`); load(); toast.success("Admin removed."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (rows === null) return <Center />;
  return (
    <div className="grid lg:grid-cols-2 gap-6 mm-fade-in">
      <Panel>
        <div className="text-sm font-medium text-slate-300 mb-1">Add a new admin</div>
        <p className="text-xs text-slate-500 mb-4">They can sign in at <span className="font-mono text-slate-400">/admin</span> with this email + password.</p>
        <div className="space-y-3">
          <Input data-testid="admin-new-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-slate-950 border-slate-700 h-10" />
          <Input data-testid="admin-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (optional)" className="bg-slate-950 border-slate-700 h-10" />
          <Input data-testid="admin-new-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password (min 8 chars)" className="bg-slate-950 border-slate-700 h-10" />
          <Button data-testid="admin-add-btn" onClick={add} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Add admin</>}
          </Button>
        </div>
      </Panel>
      <Panel>
        <div className="text-sm font-medium text-slate-300 mb-4">Admins ({rows.length})</div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto mm-scroll">
          {rows.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3" data-testid="admin-row">
              <div className="min-w-0">
                <div className="font-medium text-slate-100 truncate flex items-center gap-2">
                  {a.name || a.email}
                  {a.is_seed && <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 border border-emerald-500/30 rounded-full px-1.5 py-0.5">Primary</span>}
                  {a.is_you && <span className="text-[10px] font-mono uppercase tracking-wider text-sky-400 border border-sky-500/30 rounded-full px-1.5 py-0.5">You</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{a.email}</div>
              </div>
              {a.is_seed || a.is_you ? (
                <span className="text-slate-700 p-1 shrink-0" title={a.is_you ? "You can't remove yourself" : "The primary admin can't be removed"}><Trash2 className="h-4 w-4" /></span>
              ) : (
                <button data-testid="admin-remove-btn" onClick={() => remove(a.id, a.email)} className="text-slate-500 hover:text-rose-400 p-1 shrink-0"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const Center = () => <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div>;